import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";
import { Save, Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Copy, Edit2, GripVertical, X } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TABS = [
  { key: "cards", label: "Service Cards" },
  { key: "hero", label: "Hero" },
  { key: "intro", label: "Intro" },
  { key: "process", label: "Process" },
  { key: "stats", label: "Statistics" },
  { key: "highlights", label: "Highlights" },
  { key: "testimonials", label: "Testimonials" },
  { key: "cta", label: "CTA" },
  { key: "faqs", label: "FAQs" },
  { key: "seo", label: "SEO" },
];

const ICON_OPTIONS = ["Sparkles", "GraduationCap", "Briefcase", "Building2", "Handshake", "Users", "Target", "Award", "Zap", "Brain", "Rocket", "Shield", "Star", "TrendingUp", "MessageCircle", "PenTool", "Compass", "Layers", "Flame", "Gem"];

function SortableRow({ id, service, courses, onEdit, onDuplicate, onDelete, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style}
         className="flex items-center gap-3 p-4 rounded-xl border border-neutral-200 bg-white"
         data-testid={`service-row-${service.slug}`}>
      <button {...attributes} {...listeners} className="cursor-grab p-1 hover:bg-neutral-100 rounded" data-testid={`service-drag-${service.slug}`}>
        <GripVertical className="w-4 h-4 text-neutral-400" />
      </button>
      <div className="w-10 h-10 rounded-lg bg-neutral-900 text-sky-300 flex items-center justify-center text-xs font-bold shrink-0">
        {service.icon?.slice(0, 2) || "SV"}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">{service.title} {service.featured && <span className="text-yellow-500">★</span>}</div>
        <div className="text-xs text-neutral-500 truncate">{service.short_description}</div>
        {service.related_course_slugs?.length > 0 && (
          <div className="text-[10px] text-sky-600 mt-1">Linked to: {service.related_course_slugs.join(", ")}</div>
        )}
      </div>
      <button onClick={() => onToggle(service)} className={`p-1.5 rounded text-xs ${service.published ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`}
              data-testid={`service-toggle-${service.slug}`} title={service.published ? "Published" : "Draft"}>
        {service.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
      <button onClick={() => onEdit(service)} className="p-1.5 hover:bg-neutral-100 rounded" data-testid={`service-edit-${service.slug}`}>
        <Edit2 className="w-4 h-4" />
      </button>
      <button onClick={() => onDuplicate(service)} className="p-1.5 hover:bg-neutral-100 rounded" data-testid={`service-duplicate-${service.slug}`}>
        <Copy className="w-4 h-4" />
      </button>
      <button onClick={() => onDelete(service)} className="p-1.5 hover:bg-red-100 text-red-600 rounded" data-testid={`service-delete-${service.slug}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

function ServiceModal({ service, courses, onClose, onSave }) {
  const [form, setForm] = useState(service);
  const change = (k, v) => setForm({ ...form, [k]: v });
  const toggleCourse = (slug) => {
    const cur = form.related_course_slugs || [];
    change("related_course_slugs", cur.includes(slug) ? cur.filter(s => s !== slug) : [...cur, slug]);
  };
  const submit = (e) => { e.preventDefault(); onSave(form); };
  const cls = "w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm";
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="service-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-xl">{form.id ? "Edit" : "New"} Service</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="grid gap-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold">Title *</label><input required value={form.title || ""} onChange={(e) => change("title", e.target.value)} className={cls} data-testid="service-field-title" /></div>
            <div><label className="text-xs font-semibold">Slug *</label><input required value={form.slug || ""} onChange={(e) => change("slug", e.target.value)} className={cls} data-testid="service-field-slug" /></div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold">Icon (lucide)</label>
              <select value={form.icon || "Sparkles"} onChange={(e) => change("icon", e.target.value)} className={cls}>
                {ICON_OPTIONS.map(i => <option key={i}>{i}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-semibold">Image URL (optional)</label><input value={form.image || ""} onChange={(e) => change("image", e.target.value)} className={cls} /></div>
          </div>
          <div><label className="text-xs font-semibold">Short description *</label><textarea required rows={2} value={form.short_description || ""} onChange={(e) => change("short_description", e.target.value)} className={cls} /></div>
          <div><label className="text-xs font-semibold">Full description</label><textarea rows={4} value={form.description || ""} onChange={(e) => change("description", e.target.value)} className={cls} /></div>
          <div><label className="text-xs font-semibold">Feature bullets (comma-separated)</label>
            <input value={Array.isArray(form.features) ? form.features.join(", ") : (form.features || "")} onChange={(e) => change("features", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} className={cls} /></div>
          <div className="grid md:grid-cols-3 gap-3">
            <div><label className="text-xs font-semibold">Button text</label><input value={form.button_text || ""} onChange={(e) => change("button_text", e.target.value)} className={cls} /></div>
            <div className="md:col-span-2"><label className="text-xs font-semibold">Button link (URL or /path)</label><input value={form.button_link || ""} onChange={(e) => change("button_link", e.target.value)} className={cls} /></div>
          </div>
          <div>
            <label className="text-xs font-semibold">Related courses</label>
            <div className="mt-2 grid md:grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border border-neutral-200 rounded-lg">
              {courses.map(c => {
                const on = (form.related_course_slugs || []).includes(c.slug);
                return (
                  <label key={c.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer text-xs ${on ? "bg-sky-50 border border-sky-200" : "bg-neutral-50"}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleCourse(c.slug)} />
                    <span className="truncate">{c.title}</span>
                  </label>
                );
              })}
              {courses.length === 0 && <div className="text-xs text-neutral-500 col-span-2">No courses yet</div>}
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!form.featured} onChange={(e) => change("featured", e.target.checked)} /> Featured on Home/About</label>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.button_visible !== false} onChange={(e) => change("button_visible", e.target.checked)} /> Show button</label>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.published !== false} onChange={(e) => change("published", e.target.checked)} /> Published</label>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold">Meta title (SEO)</label><input value={form.meta_title || ""} onChange={(e) => change("meta_title", e.target.value)} className={cls} /></div>
            <div><label className="text-xs font-semibold">Meta description (SEO)</label><input value={form.meta_description || ""} onChange={(e) => change("meta_description", e.target.value)} className={cls} /></div>
          </div>
        </div>
        <button type="submit" className="btn-primary-eq w-full justify-center mt-6" data-testid="service-save">Save Service</button>
      </form>
    </div>
  );
}

export default function AdminServices() {
  const { refresh } = useSettings();
  const [data, setData] = useState(null);
  const [services, setServices] = useState([]);
  const [courses, setCourses] = useState([]);
  const [tab, setTab] = useState("cards");
  const [editing, setEditing] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const load = () => Promise.all([
    api.get("/settings").then(({ data }) => setData(data)),
    api.get("/admin/services").then(({ data }) => setServices(data)),
    api.get("/courses").then(({ data }) => setCourses(data)),
  ]);
  useEffect(() => { load(); }, []);
  if (!data) return <div className="p-8">Loading...</div>;

  const set = (path, value) => {
    const clone = JSON.parse(JSON.stringify(data));
    const parts = path.split(".");
    let cur = clone;
    for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]] = cur[parts[i]] || {};
    cur[parts[parts.length - 1]] = value;
    setData(clone);
  };
  const save = async () => {
    try { await api.put("/settings", { data }); toast.success("Services page saved"); refresh(); }
    catch { toast.error("Save failed"); }
  };
  const F = ({ label, path, type = "text", rows }) => {
    const parts = path.split("."); let val = data;
    for (const p of parts) val = val?.[p] ?? "";
    const cls = "w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm";
    if (type === "textarea") return <div><label className="text-xs font-semibold text-neutral-600">{label}</label><textarea rows={rows || 3} value={val} onChange={(e) => set(path, e.target.value)} className={cls} data-testid={`services-${path}`} /></div>;
    if (type === "checkbox") return <label className="flex items-center gap-2"><input type="checkbox" checked={!!val} onChange={(e) => set(path, e.target.checked)} data-testid={`services-${path}`} /><span className="text-sm">{label}</span></label>;
    return <div><label className="text-xs font-semibold text-neutral-600">{label}</label><input type={type} value={val} onChange={(e) => set(path, e.target.value)} className={cls} data-testid={`services-${path}`} /></div>;
  };

  const sp = data.services_page || {};

  // Service card CRUD
  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = services.findIndex(s => s.id === active.id);
    const newIdx = services.findIndex(s => s.id === over.id);
    const next = arrayMove(services, oldIdx, newIdx);
    setServices(next);
    await api.post("/admin/services/reorder", { ordered_ids: next.map(s => s.id) });
    toast.success("Order updated");
  };
  const saveService = async (form) => {
    try {
      const payload = { ...form };
      if (typeof payload.features === "string") payload.features = payload.features.split(",").map(s => s.trim()).filter(Boolean);
      if (form.id) await api.patch(`/admin/services/${form.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error("Save failed: " + (e.response?.data?.detail || "error")); }
  };
  const duplicateService = async (s) => {
    if (!window.confirm(`Duplicate "${s.title}"?`)) return;
    await api.post(`/admin/services/${s.id}/duplicate`);
    toast.success("Duplicated"); load();
  };
  const deleteService = async (s) => {
    if (!window.confirm(`Delete "${s.title}"? This cannot be undone.`)) return;
    await api.delete(`/admin/services/${s.id}`);
    toast.success("Deleted"); load();
  };
  const togglePublished = async (s) => {
    await api.patch(`/admin/services/${s.id}`, { published: !s.published });
    load();
  };
  const newService = () => setEditing({
    title: "", slug: "", icon: "Sparkles", short_description: "", description: "",
    features: [], button_text: "Learn More", button_link: "/contact", button_visible: true,
    related_course_slugs: [], order: services.length, featured: false, published: true,
  });

  // Generic array editor for FAQs, process steps, stats, highlights
  const arrayEditor = (path, template, fields) => {
    const arr = sp[path] || [];
    const setArr = (next) => set(`services_page.${path}`, next);
    return (
      <div className="space-y-3">
        {arr.map((item, i) => (
          <div key={i} className="p-3 rounded-xl border border-neutral-200 grid gap-2" data-testid={`${path}-row-${i}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-neutral-500">#{i + 1}</div>
              <div className="flex gap-1">
                <button onClick={() => { const a = [...arr]; if (i > 0) [a[i], a[i-1]] = [a[i-1], a[i]]; setArr(a); }} disabled={i === 0} className="p-1.5 hover:bg-neutral-100 rounded disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => { const a = [...arr]; if (i < arr.length-1) [a[i], a[i+1]] = [a[i+1], a[i]]; setArr(a); }} disabled={i === arr.length-1} className="p-1.5 hover:bg-neutral-100 rounded disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => setArr(arr.filter((_, x) => x !== i))} className="p-1.5 hover:bg-red-100 text-red-600 rounded" data-testid={`${path}-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {fields.map((f) => (
                <div key={f.key} className={f.wide ? "md:col-span-2" : ""}>
                  <label className="text-xs text-neutral-600">{f.label}</label>
                  {f.type === "textarea"
                    ? <textarea rows={2} value={item[f.key] || ""} onChange={(e) => setArr(arr.map((x, k) => k === i ? { ...x, [f.key]: e.target.value } : x))} className="w-full mt-1 px-2 py-1.5 border border-neutral-200 rounded text-sm" data-testid={`${path}-${f.key}-${i}`} />
                    : f.type === "select"
                      ? <select value={item[f.key] || ""} onChange={(e) => setArr(arr.map((x, k) => k === i ? { ...x, [f.key]: e.target.value } : x))} className="w-full mt-1 px-2 py-1.5 border border-neutral-200 rounded text-sm" data-testid={`${path}-${f.key}-${i}`}>
                          {f.options.map(o => <option key={o}>{o}</option>)}
                        </select>
                      : <input value={item[f.key] || ""} onChange={(e) => setArr(arr.map((x, k) => k === i ? { ...x, [f.key]: e.target.value } : x))} className="w-full mt-1 px-2 py-1.5 border border-neutral-200 rounded text-sm" data-testid={`${path}-${f.key}-${i}`} />
                  }
                </div>
              ))}
            </div>
          </div>
        ))}
        <button onClick={() => setArr([...arr, { ...template }])} className="btn-outline-eq text-sm" data-testid={`${path}-add`}><Plus className="w-4 h-4" /> Add</button>
      </div>
    );
  };

  return (
    <div className="p-8 pb-24 max-w-6xl" data-testid="admin-services-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black text-3xl" data-testid="admin-services-title">Services Page</h1>
          <p className="text-neutral-500 mt-1">Fully CMS-driven. Every section, card, image, link and SEO field is edited here.</p>
        </div>
        <div className="flex gap-2">
          <a href="/services" target="_blank" rel="noreferrer" className="btn-outline-eq text-sm">Preview →</a>
          <button onClick={save} className="btn-primary-eq text-sm" data-testid="services-save"><Save className="w-4 h-4" /> Save Page Content</button>
        </div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-100"}`}
            data-testid={`services-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      <div className="bg-white rounded-2xl p-6 border border-neutral-200">
        {tab === "cards" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-neutral-500">Drag ⋮ to reorder. Toggle publish, duplicate, edit or delete inline.</p>
              <button onClick={newService} className="btn-primary-eq text-sm" data-testid="service-add"><Plus className="w-4 h-4" /> New Service</button>
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={services.map(s => s.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {services.map(s => (
                    <SortableRow key={s.id} id={s.id} service={s} courses={courses}
                                 onEdit={setEditing} onDuplicate={duplicateService} onDelete={deleteService} onToggle={togglePublished} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            {services.length === 0 && <div className="text-center text-neutral-500 py-16">No services yet. Click "New Service" to add one.</div>}
          </div>
        )}

        {tab === "hero" && (<div className="grid gap-4">
          <F label="Kicker (small overhead label)" path="services_page.hero_kicker" />
          <F label="Hero title" path="services_page.hero_title" type="textarea" rows={2} />
          <F label="Hero subtitle" path="services_page.hero_subtitle" type="textarea" />
          <F label="Hero background image URL" path="services_page.hero_image" />
          {sp.hero_image && <img src={sp.hero_image} alt="" className="w-full max-h-56 object-cover rounded-xl mt-2" />}
          <div className="grid md:grid-cols-2 gap-3">
            <F label="CTA button text" path="services_page.hero_cta_text" />
            <F label="CTA button link (URL or /path)" path="services_page.hero_cta_link" />
          </div>
          <F label="Show hero CTA button" path="services_page.hero_cta_visible" type="checkbox" />
        </div>)}

        {tab === "intro" && (<div className="grid gap-4">
          <F label="Show intro section" path="services_page.intro_visible" type="checkbox" />
          <F label="Kicker" path="services_page.intro_kicker" />
          <F label="Heading" path="services_page.intro_heading" type="textarea" rows={2} />
          <F label="Body copy" path="services_page.intro_body" type="textarea" rows={4} />
        </div>)}

        {tab === "process" && (<div>
          <F label="Show process section" path="services_page.process_visible" type="checkbox" />
          <F label="Section heading" path="services_page.process_heading" />
          <div className="mt-4">{arrayEditor("process_steps", { title: "", description: "", icon: "Rocket" },
            [{ key: "title", label: "Step title" }, { key: "icon", label: "Icon", type: "select", options: ICON_OPTIONS }, { key: "description", label: "Description", type: "textarea", wide: true }])}</div>
        </div>)}

        {tab === "stats" && (<div>
          <F label="Show statistics section" path="services_page.stats_visible" type="checkbox" />
          <F label="Section heading" path="services_page.stats_heading" />
          <div className="mt-4">{arrayEditor("stats", { metric: "", label: "", icon: "TrendingUp" },
            [{ key: "metric", label: "Metric (e.g. 5,000+)" }, { key: "label", label: "Label" }, { key: "icon", label: "Icon", type: "select", options: ICON_OPTIONS }])}</div>
        </div>)}

        {tab === "highlights" && (<div>
          <F label="Show highlights section" path="services_page.highlights_visible" type="checkbox" />
          <F label="Section heading" path="services_page.highlights_heading" />
          <div className="mt-4">{arrayEditor("highlights", { title: "", description: "", icon: "Sparkles" },
            [{ key: "title", label: "Title" }, { key: "icon", label: "Icon", type: "select", options: ICON_OPTIONS }, { key: "description", label: "Description", type: "textarea", wide: true }])}</div>
        </div>)}

        {tab === "testimonials" && (<div className="grid gap-4">
          <F label="Show testimonials section" path="services_page.testimonials_visible" type="checkbox" />
          <F label="Section heading" path="services_page.testimonials_heading" />
          <p className="text-sm text-neutral-500">Testimonials are pulled live from the global <a href="/admin/testimonials" className="text-sky-600 underline">Testimonials manager</a>.</p>
        </div>)}

        {tab === "cta" && (<div className="grid gap-4">
          <F label="Show CTA section" path="services_page.cta_visible" type="checkbox" />
          <F label="Heading" path="services_page.cta_heading" />
          <F label="Body" path="services_page.cta_body" type="textarea" />
          <div className="grid md:grid-cols-2 gap-3">
            <F label="Button text" path="services_page.cta_button_text" />
            <F label="Button link" path="services_page.cta_button_link" />
          </div>
          <F label="Background image URL (optional)" path="services_page.cta_background_image" />
        </div>)}

        {tab === "faqs" && (<div>
          <F label="Show FAQs section" path="services_page.faqs_visible" type="checkbox" />
          <F label="Section heading" path="services_page.faqs_heading" />
          <div className="mt-4">{arrayEditor("faqs", { q: "", a: "" },
            [{ key: "q", label: "Question", wide: true }, { key: "a", label: "Answer", type: "textarea", wide: true }])}</div>
        </div>)}

        {tab === "seo" && (<div className="grid gap-4">
          <F label="Meta title" path="services_page.seo_meta_title" />
          <F label="Meta description" path="services_page.seo_meta_description" type="textarea" />
          <F label="Keywords" path="services_page.seo_keywords" />
          <F label="Open Graph image URL" path="services_page.seo_og_image" />
          <F label="URL slug" path="services_page.seo_slug" />
        </div>)}
      </div>

      {editing && <ServiceModal service={editing} courses={courses} onClose={() => setEditing(null)} onSave={saveService} />}
    </div>
  );
}
